import { connect } from 'react-redux';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Dropdown, ListView, SearchBar } from '@commons/utils/styledLibraryComponents';
import {
  GENERIC_MODAL_BACK_BUTTON,
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CONFIRM_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import EmptyState from '@commons/EmptyState';
import normalizeStringValue from '@commons/utils/normalizeStringValue';

import { canEditExternalClient } from '@selectors/actions/externalClientActions';
import { EXTERNAL_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { getAuthorizedActions } from '@selectors/featureProps';

import { externalClient as externalClientService } from '@services/externalClient';

import { Container, HeaderContainer } from './styledComponents';
import { exportProductExternalClients } from './utils/export';
import { getActionsSettings, getRowActionsSettings } from './utils/getActions';
import { getColumns } from './utils/columns';
import { getPriceModalParams } from './utils/getPriceModalParams';
import ExternalClientSelectionList from './components/ExternalClientSelectionList';

const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_TYPE = 'asc';

// Platforms can have dozens of stores: cap the panel height so it does not overflow the page
const STORE_DROPDOWN_LIST_STYLE = { maxHeight: '200px' };

const CLOSED_PRICE_MODAL = { isOpen: false, isBulk: false, externalClientIds: [] };

/**
 * "Catalogues B2B" tab of the product detail (sales point and central kitchen): full list of the
 * external clients having the product in their catalog, with catalog price and margin. The list is
 * not paginated: search and sorting are handled client-side. The catalog mutations (add / remove /
 * price edition) require the external-client edit right.
 */
const ProductExternalClients = (props) => {
  const {
    product,
    isKitchenProduct,
    isReadOnly,
    activeStores,
    currency,
    canEditExternalClients,
    isGenericModalOpen,
    openGenericModal,
    refreshGenericModal,
    showSuccessMessage,
    showErrorMessage,
  } = props;

  const canEdit = canEditExternalClients && !isReadOnly;

  // Store filter scope: central kitchens only on a central product, sales points otherwise
  const stores = useMemo(
    () => activeStores.filter(({ isKitchen }) => !!isKitchen === !!isKitchenProduct),
    [activeStores, isKitchenProduct],
  );

  const [externalClients, setExternalClients] = useState([]);
  const [defaultChannel, setDefaultChannel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [search, setSearch] = useState('');

  const [selectedStores, setSelectedStores] = useState(stores);
  const [selectedClients, setSelectedClients] = useState([]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pickerSelectedClientIds, setPickerSelectedClientIds] = useState([]);

  const [priceModal, setPriceModal] = useState(CLOSED_PRICE_MODAL);
  const [price, setPrice] = useState('');

  const selectedStoreIds = useMemo(() => selectedStores.map(({ id }) => id), [selectedStores]);

  const defaultChannelName = defaultChannel
    ? i18next.t(`${defaultChannel.translationKey}.FULL`, { defaultValue: defaultChannel.name })
    : '';

  const columns = useMemo(() => getColumns(defaultChannelName), [defaultChannelName]);

  // activeStores is filled by an async fetch, so the initial value can be empty: resync the filter
  // whenever the scoped stores land, otherwise "all stores selected" would stay permanently empty.
  useEffect(() => {
    setSelectedStores(stores);
  }, [stores]);

  const fetchExternalClients = useCallback(async () => {
    setSelectedClients([]);

    if (!selectedStoreIds.length) {
      setExternalClients([]);
      setDefaultChannel(null);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);

    try {
      const result = await externalClientService.getProductExternalClients(product.id, {
        storeIds: selectedStoreIds,
      });

      setExternalClients(result.externalClients || []);
      setDefaultChannel(result.defaultChannel || null);
    } catch {
      setExternalClients([]);
      setDefaultChannel(null);
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_FETCH_ERROR'));
    } finally {
      setIsLoading(false);
    }
  }, [product.id, selectedStoreIds, showErrorMessage]);

  useEffect(() => {
    fetchExternalClients();
  }, [fetchExternalClients]);

  const filteredExternalClients = useMemo(() => {
    if (!search) {
      return externalClients;
    }

    const normalizedSearch = normalizeStringValue(search);

    return externalClients.filter(
      ({ name, externalReference }) =>
        normalizeStringValue(name || '').includes(normalizedSearch) ||
        normalizeStringValue(externalReference || '').includes(normalizedSearch),
    );
  }, [externalClients, search]);

  /*******************/
  /* Catalog add     */
  /*******************/

  const resetAddModal = () => {
    setIsAddModalOpen(false);
    setPickerSelectedClientIds([]);
  };

  const handleConfirmAdd = async () => {
    const externalClientIds = pickerSelectedClientIds;

    resetAddModal();

    try {
      await externalClientService.addProductsToCatalogs(externalClientIds, [product.id]);

      showSuccessMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_ADD_SUCCESS'));

      await fetchExternalClients();
    } catch {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_ADD_ERROR'));
    }
  };

  const getAddModalParams = () => ({
    type: 'actionWhite',
    isFullscreen: true,
    title: i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_ADD_MODAL_TITLE'),
    icon: '/images/inpulse/add-black-small.svg',
    customPadding: '24px 0px 0px 0px',
    handleCloseCleanUp: resetAddModal,
    component: ExternalClientSelectionList,
    data: {
      stores,
      excludedProductId: product.id,
      selectedClientIds: pickerSelectedClientIds,
      onSelectionChange: setPickerSelectedClientIds,
      onFetchError: () =>
        showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_CLIENTS_FETCH_ERROR')),
    },
    actions: [
      {
        ...GENERIC_MODAL_BACK_BUTTON(),
        handleClick: resetAddModal,
      },
      {
        ...GENERIC_MODAL_SAVE_BUTTON(),
        isDisabled: !pickerSelectedClientIds.length,
        handleClick: handleConfirmAdd,
      },
    ],
  });

  useEffect(() => {
    if (!isAddModalOpen) {
      return;
    }

    const params = getAddModalParams();

    if (isGenericModalOpen) {
      refreshGenericModal(params);

      return;
    }

    openGenericModal(params);
  }, [isAddModalOpen, pickerSelectedClientIds]);

  /*******************/
  /* Price edition   */
  /*******************/

  const resetPriceModal = () => {
    setPriceModal(CLOSED_PRICE_MODAL);
    setPrice('');
  };

  const handleConfirmPriceUpdate = async () => {
    const { externalClientIds } = priceModal;
    const newPrice = parseFloat(price);

    resetPriceModal();

    try {
      await externalClientService.updateCatalogProductsPrice(
        externalClientIds,
        [product.id],
        newPrice,
      );

      showSuccessMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_EDIT_PRICE_SUCCESS'));

      await fetchExternalClients();
    } catch {
      showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_EDIT_PRICE_ERROR'));
    }
  };

  useEffect(() => {
    if (!priceModal.isOpen) {
      return;
    }

    const params = getPriceModalParams({
      isBulk: priceModal.isBulk,
      price,
      onPriceChange: setPrice,
      onSave: handleConfirmPriceUpdate,
    });

    if (isGenericModalOpen) {
      refreshGenericModal(params);

      return;
    }

    openGenericModal(params);
  }, [priceModal, price]);

  const handleEditPrices = useCallback((targetedClients) => {
    setPrice('');
    setPriceModal({
      isOpen: true,
      isBulk: targetedClients.length > 1,
      externalClientIds: targetedClients.map(({ id }) => id),
    });
  }, []);

  /*******************/
  /* Catalog removal */
  /*******************/

  const handleConfirmRemoval = useCallback(
    async (targetedClients) => {
      try {
        await externalClientService.removeProductsFromCatalogs(
          targetedClients.map(({ id }) => id),
          [product.id],
        );

        showSuccessMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_REMOVE_SUCCESS'));

        await fetchExternalClients();
      } catch {
        showErrorMessage(i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_REMOVE_ERROR'));
      }
    },
    [product.id, fetchExternalClients, showSuccessMessage, showErrorMessage],
  );

  const handleRemoveFromCatalogs = useCallback(
    (targetedClients) => {
      openGenericModal(
        getConfirmationModal({
          type: 'warning',
          title: i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_REMOVE_MODAL_TITLE'),
          icon: '/images/inpulse/trash-white-small.svg',
          content: i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_REMOVE_MODAL_CONTENT'),
          actions: [
            GENERIC_MODAL_CANCEL_BUTTON(),
            {
              ...GENERIC_MODAL_CONFIRM_BUTTON(),
              handleClick: () => handleConfirmRemoval(targetedClients),
            },
          ],
        }),
      );
    },
    [handleConfirmRemoval, openGenericModal],
  );

  const handleAddToCatalogs = useCallback(() => setIsAddModalOpen(true), []);

  const handleExport = useCallback(() => {
    const clientsToExport = selectedClients.length ? selectedClients : filteredExternalClients;

    exportProductExternalClients(clientsToExport, currency);
  }, [selectedClients, filteredExternalClients, currency]);

  /*******************/
  /* List handlers   */
  /*******************/

  const actions = useMemo(
    () =>
      getActionsSettings({
        canEdit,
        selectedClients,
        handleAddToCatalogs,
        handleEditPrices,
        handleRemoveFromCatalogs,
        handleExport,
      }),
    [
      canEdit,
      selectedClients,
      handleAddToCatalogs,
      handleEditPrices,
      handleRemoveFromCatalogs,
      handleExport,
    ],
  );

  const rowActions = useMemo(
    () => getRowActionsSettings({ canEdit, handleEditPrices, handleRemoveFromCatalogs }),
    [canEdit, handleEditPrices, handleRemoveFromCatalogs],
  );

  return (
    <Container>
      <ListView
        actions={actions}
        columns={columns}
        data={filteredExternalClients}
        defaultOrderBy={DEFAULT_ORDER_BY}
        defaultOrderType={DEFAULT_ORDER_TYPE}
        isLoading={isLoading}
        minActionsInActionsDropdown={1}
        padding={'6px 24px 0px 24px'}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <HeaderContainer>
            <SearchBar
              placeholder={i18next.t('GENERAL.SEARCH')}
              setValue={setSearch}
              value={search}
            />
            <Dropdown
              customDropdownListStyle={STORE_DROPDOWN_LIST_STYLE}
              iconSrc={'/images/inpulse/store-black-small.svg'}
              isUniqueSelection={false}
              items={stores}
              placeholder={i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_STORES')}
              selectedItems={selectedStores}
              isRequired
              onSelectionChange={setSelectedStores}
            />
          </HeaderContainer>
        )}
        rowActions={rowActions}
        setSelectedItems={setSelectedClients}
        disableFooter
        forceEnableSelection
        hideSearchbar
      />
    </Container>
  );
};

ProductExternalClients.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  isKitchenProduct: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

ProductExternalClients.defaultProps = {
  isKitchenProduct: false,
  isReadOnly: false,
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_FEATURE_PATHS.EXTERNAL_CLIENTS,
  );

  return {
    activeStores: state.baseReducer.activeStores,
    currency: state.baseReducer.currency,
    isGenericModalOpen: state.modalReducer.GenericModalBool,
    canEditExternalClients:
      state.baseReducer.user.isDeepsight || canEditExternalClient(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductExternalClients);
